'use client'
import { ArrowUp, Github, Linkedin, MessageCircle } from 'lucide-react'
import React from 'react'
import { motion } from 'framer-motion'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { handleSignUpClick } from '@/lib/tracking'
import { scrollToSection } from '@/lib/scroll'

// Custom X icon component with smaller size
const XIcon = ({ className }: any) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    width: '24px', 
    height: '24px' 
  }}>
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      width="18" 
      height="18" 
      style={{ 
        minWidth: '18px', 
        minHeight: '18px', 
        maxWidth: '18px', 
        maxHeight: '18px' 
      }}
      fill="currentColor"
      className={className}
    >
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  </div>
)

// Custom Discord icon component with outlined style
const DiscordIcon = ({ className }: any) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24" 
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="22"
    height="22"
  >
    <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.25-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12z"/>
  </svg>
)

const Footer = () => {
	const router = useRouter()

	const handleScroll = (e:any, target:any) => {
		e.preventDefault()

		if (target.startsWith('#')) {
			scrollToSection(target)
		} else {
			router.push(target)
		}
	}

	const LINKS = [
		{
			category: 'Product',
			items: [
				{
					name: 'Features',
					link: '#features',
					onClick: (e:any, link:any) => handleScroll(e, link),
				},
				{
					name: 'Pricing',
					link: '#pricing',
					onClick: (e:any, link:any) => handleScroll(e, link),
				},
				{
					name: 'Support',
					link: '#contact',
					onClick: (e:any, link:any) => handleScroll(e, link),
				},
			],
		},
		{
			category: 'Company',
			items: [
				{
					name: 'Community',
					link: '#',
					onClick: () => {
						window.open('https://discord.com/invite/SR4V7yrWBt', '_blank')
					},
				},
			],
		}
	]

	return (
		<footer className="bg-white border-t border-gray-100">
			{/* Animated gradient top border */}
			<div className="h-1 bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] animate-gradient-x" />

			<div className="max-w-7xl mx-auto px-4 pt-12">
				<div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-12">
					{/* Logo */}
					<div className="mb-6 md:mb-0">
						<Link href="/">
							<Image 
								src="/logo_dark.svg" 
								alt="Infinitcode Logo" 
								width={180} 
								height={45} 
								className="h-10 w-auto"
							/>
						</Link>
					</div>
					
					{/* Try Free Button */}
					<Link onClick={handleSignUpClick} href={'https://app.infinitcode.ai/'} target='_blank'>
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							className="px-6 py-3 bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
						>
							Start Your 3-Day Free Trial
						</motion.button>
					</Link>
				</div>

				<div className="mx-auto grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-8 mb-16">
					{/* Nav Columns */}
					{LINKS.map((link) => {
						return (
							<div key={link.category} className="space-y-4">
								<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">{link.category}</h3>
								<ul className="space-y-2">
									{link.items.map((item) => {
										return (
											<li key={item.name}>
												<button
													onClick={(e) => item.onClick(e, item.link)}
													className="text-gray-500 hover:text-[#3BCF8E] transition-colors duration-200"
												>
													{item.name}
												</button>
											</li>
										)
									})}
								</ul>
							</div>
						)
					})}

					<div className="space-y-4 md:col-span-2 lg:col-span-1">
						<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Connect</h3>
						<div className="flex space-x-4">
							{[
								['X', 'https://x.com/Infinitcode_ai', XIcon],
								['LinkedIn', 'https://www.linkedin.com/company/infinitcode-ai/posts/?feedView=all', Linkedin],
								['Discord', 'https://discord.com/invite/SR4V7yrWBt', DiscordIcon],
							].map(([name, href, Icon]: any) => (
								<motion.a
									key={name}
									href={href}
									target="_blank"
									rel="noopener noreferrer"
									aria-label={`Visit ${name}`}
									className="p-2 rounded-lg bg-gray-100 hover:bg-[#3BCF8E]/10 transition-colors duration-200"
									whileHover={{ scale: 1.1 }}
									whileTap={{ scale: 0.9 }}
								>
									<span className="sr-only">{name}</span>
									{typeof Icon === 'function' 
										? <Icon className="h-6 w-6 text-gray-600 hover:text-[#3BCF8E] transition-colors" />
										: React.createElement(Icon, { className: "h-6 w-6 text-gray-600 hover:text-[#3BCF8E] transition-colors" })
									}
								</motion.a>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Copyright Bottom Bar */}
			<div className="bg-gray-50 py-4">
				<div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
					<div className="mb-2 md:mb-0">© {new Date().getFullYear()} Copyrights <span className='hover:text-[#3BCF8E] transition-colors'>Infinitcode.</span> All rights reserved</div>
					<div className="flex space-x-4">
						<Link href="/cookies-policy" className="hover:text-[#3BCF8E] transition-colors">
							Cookies Policy
						</Link>
						<Link href="/privacy-policy" className="hover:text-[#3BCF8E] transition-colors">
							Privacy Policy
						</Link>
						<Link href="/terms-of-service" className="hover:text-[#3BCF8E] transition-colors">
							Terms of Service
						</Link>
					</div>
				</div>
			</div>

			{/* Back to Top Button */}
			<motion.button
				className="fixed z-50 bottom-8 right-8 p-3 bg-white shadow-lg rounded-full border border-gray-200 hover:border-[#3BCF8E] transition-colors"
				whileHover={{ scale: 1.05 }}
				whileTap={{ scale: 0.95 }}
				onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
			>
				<ArrowUp className="h-5 w-5 text-gray-600" />
				<span className="sr-only">Back to top</span>
			</motion.button>
		</footer>
	)
}

export default Footer